import React, { useEffect, useState } from "react";
import { get, omitBy } from "lodash/fp";
import {
  Container,
  Pagination,
  Table,
  Footer,
  Content,
  Button,
  Form,
  Input,
  Col,
  InputGroup,
  SelectPicker,
  Checkbox,
  Header,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import SaveIcon from "@rsuite/icons/AddOutline";
import CloseIcon from "@rsuite/icons/Close";
import { SingerFields, SingerListParams } from "./SingerDataTypes";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import { updateIn } from "json-immut";
import MasterEditCell from "../../components/Elements/MasterEditCell";
import ActionCell from "./ActionCell";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { useTranslation } from "react-i18next";
import { HelperServices } from "../../../core/services/HelperServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  appConstObj,
  messageTypeArr,
  genderObjArr,
} from "../../../core/config/AppConstants";
import "./Singers.css";
import { isEmptyVal } from "../../../utils/helpers";
import CheckCell from "../../components/Elements/CheckCell";

function Singers({
  withHeader = true,
  multiSelect = true,
  showCreateForm = true,
  onSelectSinger,
  ...props
}: {
  withHeader?: boolean;
  multiSelect?: boolean;
  showCreateForm?: boolean;
  onSelectSinger?: Function;
}) {
  const { Column } = Table;
  const [loading, setLoading] = useState(false);
  const [singerId, setSingerId] = useState(0);
  const [data, setData] = useState<SingerFields[]>([]);
  const [editData, setEditData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [page, setPage] = useState(1);
  const [sort_by, setSortBy] = useState();
  const [sort_order, setSortOrder] = useState();
  const [errors, setErrors] = useState<any>({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [genderSearchInput, setGenderSearch] = useState<string>("");
  const [openNoSingerModal, setOpenNoSingerModal] = useState(false);
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    gender: "",
    status: "",
  };

  const initFormSaveData = {
    artist_name: "",
    gender: "",
    status: 1,
  };

  const [formData, setFormData] =
    useState<SingerListParams>(initSearchFormData);

  const [formSaveData, setFormSaveData] =
    useState<SingerFields>(initFormSaveData);

  const [showCreate, setShowCreate] = useState<boolean>(showCreateForm);

  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    let keys: any;
    if (!checked) {
      keys = checkedKeys.filter((item) => item !== value);
    } else if (checked && multiSelect) {
      keys = [...checkedKeys, value];
    } else if (checked && !multiSelect) {
      keys = [value];
    }
    setCheckedKeys(keys);
  };

  function setSelectedSinger() {
    if (checkedKeys.length === 0) {
      setOpenNoSingerModal(true);
      return true;
    } else {
      const singerData = data
        .filter((item) => item.id == checkedKeys[0])
        .shift();
      onSelectSinger && onSelectSinger(singerData);
    }
  }

  async function getSingers(params: SingerListParams) {
    try {
      setLoading(true);
      const res = await HelperServices.getSingers(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      if (!showCreateForm) {
        if (get("data.total", res) <= 0) {
          setShowCreate(UtilityServices.hasPermission("create-song"));
        } else {
          setShowCreate(false);
        }
      }

      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ name: smartSearchInput },
      ...{ gender: genderSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SingerListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  const handleEditData = async (id: Number) => {
    let nextData: any = {};
    let error: string = "";
    let activeItem: any = {};
    nextData = Object.assign([], data);
    activeItem = nextData?.find((item: any) => item.id === id);
    activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";

    if (activeItem.editStatus == null) {
      if (isEmptyVal(activeItem.artist_name))
        error = t("REQUIRED", {
          field: t("SINGER").toLowerCase() + " " + t("NAME").toLowerCase(),
        });
      if (isEmptyVal(activeItem.gender))
        error = t("REQUIRED_SELECT", {
          field: t("GENDER").toLowerCase(),
        });

      if (!isEmptyVal(error)) {
        UtilityServices.showToaster({
          message: error,
          type: messageTypeArr.error,
        });
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
        return;
      }

      let params = {
        artist_name: activeItem.artist_name,
        gender: activeItem.gender,
        status: activeItem.status,
      };

      try {
        setLoading(true);
        const response: any = await HelperServices.updateSinger(
          activeItem.id,
          params
        );
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        setFormSaveData(formSaveData);
        getSingers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );

        setLoading(false);
      } catch (error: any) {
        UtilityServices.showToaster({
          message: error?.message,
          type: messageTypeArr.error,
        });
        setLoading(false);
        activeItem.editStatus = activeItem?.editStatus ? null : "EDIT";
      }
    }

    setEditData(nextData);
  };

  const handleDataChanges = (id: Number, key: any, value: any) => {
    let nextData: any = {};
    nextData = Object.assign([], data);
    nextData.find((item: any) => item.id === id)[key] =
      key == "status" ? (value ? 1 : 0) : value;
    setEditData(nextData);
  };

  const clearSaveForm = () => {
    setErrors({});
    setFormSaveData(initFormSaveData);
  };

  async function addSinger() {
    try {
      let i;
      let data = { ...formSaveData };
      let dumpError = { ...errors };
      for (i in data) {
        if (data[i] === "") {
          dumpError[i] = t("FIELD_REQUIRED");
          setErrors(dumpError);
        } else {
          delete dumpError[i];
          setErrors(dumpError);
        }
      }

      if (Object.keys(dumpError).length == 0) {
        setLoading(true);
        data.artist_name = data.artist_name;
        data.gender = data.gender;
        const response: any = await HelperServices.createSinger(data);
        UtilityServices.showToaster({
          message: response?.data?.message,
        });

        setPage(1);
        setFormSaveData(initFormSaveData);
        getSingers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
        setLoading(false);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  }

  const handleFormChange = (data: any) => {
    if (data.path == "status") {
      if (data.value) {
        data.value = 1;
      } else {
        data.value = 0;
      }
    }
    let form = updateIn(formSaveData, [data.path], data.value);
    setFormSaveData(form);
  };

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  const handleDataDelete = async (singerId: number) => {
    try {
      setLoading(true);
      const response: any = await HelperServices.deleteSinger(singerId);
      if (response.status) {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
        setPage(1);
        getSingers(
          getSearchParams({
            page: page,
            sort_by: sort_by,
            sort_order: sort_order,
            rows: rows,
          })
        );
      } else {
        UtilityServices.showToaster({
          message: response?.data?.message,
        });
      }
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
      setLoading(false);
    }
  };

  function onPageChange(page: number) {
    setPage(page);
    getSingers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getSingers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  const handleChangeRows = (rows: any) => {
    setPage(1);
    setRows(rows);
    getSingers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const filterSingers = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getSingers(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setGenderSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSingers(
      getSearchParams({
        name: "",
        gender: "",
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  async function init() {
    try {
      setShowCreate(
        UtilityServices.hasPermission("create-song") && showCreateForm
      );
    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
    getSingers(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }, []);

  return (
    <div className={`singers singer ${withHeader ? "singers-page" : "singers-picker"}`}>
      <Container className="container">
        {withHeader && (
          <Header className="header singer-page-header">
            <h4>{t("SINGERS")}</h4>
          </Header>
        )}
        <Content className="singers-content">
          {showCreate && (
            <Form formValue={formSaveData} className="entity-picker-createbar">
              <Col md={6} className="entity-picker-create-field">
                <Form.Group controlId="artist_name">
                  <Form.Control
                    name="artist_name"
                    size="sm"
                    placeholder={t("SINGER") + " " + t("NAME")}
                    onChange={(v: any) =>
                      handleFormChange({ path: "artist_name", value: v })
                    }
                    value={formSaveData?.artist_name}
                  />
                </Form.Group>
                <span className="text-danger">{errors["artist_name"]}</span>
              </Col>
              <Col md={4} className="entity-picker-create-field">
                <Form.Group controlId="gender">
                  <SelectPicker
                    data={genderObjArr}
                    valueKey="value"
                    labelKey="label"
                    cleanable={false}
                    onSelect={(value, item) => {
                      handleFormChange({ path: "gender", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChange({
                        path: "gender",
                        value: "",
                      });
                    }}
                    name="gender"
                    value={formSaveData?.gender}
                  />
                </Form.Group>
                <span className="text-danger">{errors["gender"]}</span>
              </Col>
              <Col md={2} className="entity-picker-create-actions">
                <div className="entity-picker-create-buttons">
                  <Button title={t("ADD")} onClick={addSinger}>
                    <SaveIcon />
                  </Button>
                  <Button title={t("CLEAR")} onClick={clearSaveForm}>
                    <CloseIcon />
                  </Button>
                </div>
              </Col>
            </Form>
          )}
          <div className="row entity-picker-toolbar-row singer-controls-row">
            <div className="col-md-4 entity-picker-select-col">
              {onSelectSinger && (
                <Button
                  className="button entity-picker-select-btn"
                  appearance="primary"
                  onClick={(e: any) => setSelectedSinger()}
                >
                  {t("SELECT") + " " + t("SINGER")}
                </Button>
              )}
            </div>
            <div className="col-md-8 mb-2 text-right singer-search-col">
              <InputGroup className="entity-picker-searchbar entity-picker-searchbar-with-filter singer-smart-search">
                <SelectPicker
                  data={genderObjArr}
                  valueKey="value"
                  labelKey="label"
                  onSelect={(value, item, e) => {
                    handleSearchFormChange({ path: "gender", value });
                    setGenderSearch(value);
                    getSingers(
                      getSearchParams({
                        gender: value,
                      })
                    );
                  }}
                  onClean={(e: any) => {
                    handleSearchFormChange({ path: "gender", value: "" });
                    setGenderSearch("");
                    getSingers(
                      getSearchParams({
                        gender: "",
                      })
                    );
                  }}
                  name="gender"
                  value={genderSearchInput}
                  placeholder={t("GENDER")}
                />
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("SINGER").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterSingers(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterSingers(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          <div
            className={total > 0 ? "singer-table" : "singer-empty-wrap"}
          >
            <Table
              className="entity-picker-table"
              loading={loading}
              height={400}
              fillHeight={true}
              data={data}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              sortType={sort_order}
              bordered
              cellBordered
              hover={true}
              renderEmpty={() => (
                <div className="entity-picker-empty-state singer-empty-state">
                  <div className="entity-picker-empty-icon">0</div>
                  <h4 className="entity-picker-empty-title">
                    {t("NO_ENTITY_FOUND", { entity: t("SINGER") })}
                  </h4>
                  <p className="entity-picker-empty-text">
                    {t("ADJUST_FILTER") +
                      " " +
                      t("OR") +
                      " " +
                      t("CREATE_NEW_ENTITY", { entity: t("SINGER") })}
                  </p>
                </div>
              )}
            >
          <Column width={40} align="center" fixed>
            <CustomHeaderCell>
              <div>
                {multiSelect && (
                  <Checkbox
                    inline
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={handleCheckAll}
                  />
                )}
              </div>
            </CustomHeaderCell>
            <CheckCell
              dataKey="id"
              checkedKeys={checkedKeys}
              onChange={handleCheck}
            />
          </Column>
          <Column flexGrow={1} align="left" fixed sortable>
            <CustomHeaderCell>{t("SINGER") + " " + t("NAME")}</CustomHeaderCell>
            <MasterEditCell
              dataKey="artist_name"
              onChange={handleDataChanges}
            />
          </Column>

          <Column flexGrow={1} align="center" fixed sortable>
            <CustomHeaderCell>{t("GENDER")}</CustomHeaderCell>
            <MasterEditCell dataKey="gender" onChange={handleDataChanges} />
          </Column>

          <Column flexGrow={1} align="center" sortable>
            <CustomHeaderCell>{t("STATUS")}</CustomHeaderCell>
            <MasterEditCell dataKey="status" onChange={handleDataChanges} />
          </Column>

          {showCreate ? (
            <Column flexGrow={1} align="center">
              <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
              <ActionCell
                dataKey="id"
                onClick={handleEditData}
                onDelete={(id: any) => {
                  setOpenDeleteModal(true);
                  setSingerId(id);
                }}
              />
            </Column>
          ) : (
            ""
          )}
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            total={total}
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
      </Container>
      <AlertModal
        open={openNoSingerModal}
        handleClose={(e: any) => {
          if (e) {
            setOpenNoSingerModal(false);
          }
        }}
        displayText={t("PLEASE_SELECT") + " " + t("SINGER")}
        modalTitle={t("PLEASE_SELECT")}
        cancelButton={false}
      />
      <AlertModal
        open={openDeleteModal}
        handleClose={(e: any) => {
          if (e == true) {
            handleDataDelete(singerId);
            setOpenDeleteModal(false);
          } else {
            setOpenDeleteModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DELETE").toLowerCase(),
          entity: t("SINGER").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("SINGER").toLowerCase(),
        })}
      />
    </div>
  );
}
export default Singers;
